<template>
  <v-row justify="center">
    <v-dialog
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="565"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          color="transparent"
          flat
          height="70"
          max-height="70"
        >
          <v-toolbar-title class="pl-3 brand-green-dark--text">
            <div class="brand-text-title mt-1">
              {{
                step === 'form' ? $t('Auth.RegisterDialog.Title').toLocaleUpperCase($i18n.locale) : otpModel?.type === 'sms' ? $t('Otp.Toolbar.Title.Phone').toLocaleUpperCase($i18n.locale) :
                  $t('Otp.Toolbar.Title.Email').toLocaleUpperCase($i18n.locale)
              }}
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            :height="$vuetify.breakpoint.smAndDown ? 40 : 50"
            :width="$vuetify.breakpoint.smAndDown ? 40 : 50"
            class="ma-0"
            color="brand-green-dark"
            icon
            outlined
            style="background-color: white;"
            @click="closeDialog"
          >
            <v-icon small>
              mdi-close
            </v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text
          ref="formContainer"
          class="pa-4 pt-0 pb-2"
        >
          <v-divider class="brand-green-dark" />

          <v-form
            v-if="model"
            ref="form"
            v-model="isFormValid"
            @submit.prevent
          >
            <v-stepper
              v-model="step"
              flat
            >
              <v-stepper-items>
                <v-stepper-content
                  class="pa-0"
                  step="form"
                >
                  <v-container fluid>
                    <v-row dense>
                      <v-col
                        class="py-4 brand-text-small brand-grey--text"
                        cols="12"
                      >
                        {{ $t('Auth.RegisterDialog.Subtitle') }}
                      </v-col>

                      <v-col
                        cols="12"
                        sm="6"
                      >
                        <v-text-field
                          v-model="model.Firstname"
                          :hint="$t('Auth.RegisterDialog.Field.Firstname.Hint')"
                          :label="$t('Auth.RegisterDialog.Field.Firstname.Label')"
                          :placeholder="$t('Auth.RegisterDialog.Field.Firstname.Placeholder')"
                          :rules="model.validator.vuetifyFormFieldRules('Firstname')"
                          class="mt-2"
                          clearable
                          dense
                          name="Firstname"
                          outlined
                          type="text"
                        />
                      </v-col>

                      <v-col
                        cols="12"
                        sm="6"
                      >
                        <v-text-field
                          v-model="model.Lastname"
                          :hint="$t('Auth.RegisterDialog.Field.Lastname.Hint')"
                          :label="$t('Auth.RegisterDialog.Field.Lastname.Label')"
                          :placeholder="$t('Auth.RegisterDialog.Field.Lastname.Placeholder')"
                          :rules="model.validator.vuetifyFormFieldRules('Lastname')"
                          class="mt-2"
                          clearable
                          dense
                          name="Lastname"
                          outlined
                          type="text"
                        />
                      </v-col>

                      <v-col cols="12">
                        <v-text-field
                          v-model="model.Email"
                          :hint="$t('Auth.RegisterDialog.Field.Email.Hint')"
                          :label="$t('Auth.RegisterDialog.Field.Email.Label')"
                          :placeholder="$t('Auth.RegisterDialog.Field.Email.Placeholder')"
                          :rules="model.validator.vuetifyFormFieldRules('Email')"
                          class="mt-2"
                          clearable
                          dense
                          name="Email"
                          outlined
                          type="text"
                        />
                      </v-col>

                      <v-col cols="12">
                        <vue-tel-input-vuetify
                          :hint="$t('Auth.RegisterDialog.Field.Phone.Hint')"
                          :label="$t('Auth.RegisterDialog.Field.Phone.Label')"
                          :placeholder="$t('Auth.RegisterDialog.Field.Phone.Placeholder')"
                          :preferred-countries="['gr', 'cy']"
                          :rules="model.validator.vuetifyFormFieldRules('Phone')"
                          :valid-characters-only="true"
                          class="mt-2"
                          clearable
                          dense
                          mode="significant"
                          name="mobile-phone"
                          outlined
                          text-field-classes="ml-2"
                          @input="(formattedNumber, phone) => {
                            model.Phone = phone.number.significant
                            model.PhonePrefix = phone.countryCode
                            validateForm()
                          }"
                        />
                      </v-col>

                      <template v-if="!item">
                        <v-col cols="12">
                          <v-text-field
                            v-model="model.Password"
                            :hint="$t('Auth.RegisterDialog.Field.Password.Hint')"
                            :label="$t('Auth.RegisterDialog.Field.Password.Label')"
                            :placeholder="$t('Auth.RegisterDialog.Field.Password.Placeholder')"
                            :rules="model.validator.vuetifyFormFieldRules('Password')"
                            class="mt-2"
                            clearable
                            dense
                            name="Password"
                            outlined
                            type="password"
                          />
                        </v-col>

                        <v-col cols="12">
                          <v-text-field
                            v-model="model.PasswordConfirm"
                            :hint="$t('Auth.RegisterDialog.Field.PasswordConfirm.Hint')"
                            :label="$t('Auth.RegisterDialog.Field.PasswordConfirm.Label')"
                            :placeholder="$t('Auth.RegisterDialog.Field.PasswordConfirm.Placeholder')"
                            :rules="model.validator.vuetifyFormFieldRules('PasswordConfirm')"
                            class="mt-2"
                            clearable
                            dense
                            name="PasswordConfirm"
                            outlined
                            type="password"
                          />
                        </v-col>
                      </template>

                      <v-col cols="12">
                        <v-checkbox
                          v-model="model.TermsCondition"
                          :rules="model.validator.vuetifyFormFieldRules('TermsCondition')"
                          :value="true"
                          class="mt-0"
                          name="TermsCondition"
                          required
                        >
                          <template #label>
                            <div class="brand-text-small">
                              {{ $t('Auth.RegisterDialog.Field.TermsCondition.Label') }}
                              <template v-for="(link, index) in $t('Auth.RegisterDialog.Field.TermsCondition.Links')">
                                <div
                                  :key="`link-${index}`"
                                  class="d-inline-block"
                                >
                                  <a
                                    :href="link.Url"
                                    class="d-inline-block"
                                    target="_blank"
                                    @click.stop
                                  >
                                    {{ link.Title }}
                                  </a>

                                  <div
                                    v-if="$t('Auth.RegisterDialog.Field.TermsCondition.Links').length > 0 && index < $t('Auth.RegisterDialog.Field.TermsCondition.Links').length - 1"
                                    class="d-inline-block"
                                  >
                                    &nbsp;{{ $t('Auth.RegisterDialog.Field.TermsCondition.And') }}&nbsp;
                                  </div>
                                </div>
                              </template>
                            </div>
                          </template>
                        </v-checkbox>

                        <v-checkbox
                          v-model="model.MarketingConsent"
                          :rules="model.validator.vuetifyFormFieldRules('MarketingConsent')"
                          :value="true"
                          class="mt-0"
                          name="MarketingConsent"
                          required
                        >
                          <template #label>
                            <div class="brand-text-small">
                              {{ $t('Auth.RegisterDialog.Field.MarketingConsent.Label') }}
                            </div>
                          </template>
                        </v-checkbox>
                      </v-col>

                      <v-col
                        v-if="showGenericError && genericError"
                        class="py-0"
                        cols="12"
                      >
                        <v-alert
                          border="left"
                          dense
                          text
                          type="error"
                        >
                          {{ genericError }}
                        </v-alert>
                      </v-col>
                    </v-row>

                    <v-row dense>
                      <v-col cols="12">
                        <v-btn
                          :disabled="isFormSaving"
                          :loading="isFormSaving"
                          block
                          class="brand-green-dark--text"
                          color="brand-primary"
                          depressed
                          x-large
                          @click="onRegisterClick"
                        >
                          <span class="brand-text-heading">{{ $t('Auth.Button.Register').toLocaleUpperCase($i18n.locale) }}</span>
                        </v-btn>
                      </v-col>

                      <v-col cols="12">
                        <v-btn
                          block
                          class="text-none mt-3"
                          color="brand-green-dark"
                          plain
                          small
                          @click="onLoginClick"
                        >
                          <span class="brand-text-small">{{ $t('Auth.Button.WithAccount') }}</span>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-stepper-content>

                <v-stepper-content
                  class="pa-0"
                  step="otp"
                >
                  <v-container
                    fluid
                    pb-0
                  >
                    <v-row dense>
                      <v-col cols="12">
                        <div class="mb-2">
                          {{ otpModel?.type === 'sms' ? $t('Otp.Title.Phone') : $t('Otp.Title.Email') }}
                        </div>
                        <v-otp-input
                          v-if="otpModel"
                          v-model="otpModel.otp"
                          :disabled="isFormSaving"
                          :loading="isFormSaving"
                          hide-details
                          length="6"
                          type="number"
                          @finish="onOtpValidate"
                        />

                        <div
                          v-if="parseInt(otpProgress.minute) || parseInt(otpProgress.second)"
                          class="text-end"
                        >
                          {{ $t('Otp.ExpiresIn') }} {{ `${otpProgress.minute}:${otpProgress.second}` }}
                        </div>

                        <v-alert
                          v-if="otpError"
                          class="mt-4 caption"
                          dense
                          type="error"
                        >
                          {{ otpError }}
                        </v-alert>
                      </v-col>
                    </v-row>

                    <v-row dense>
                      <v-col cols="12">
                        <v-divider class="mt-2 pb-2" />
                      </v-col>

                      <v-col cols="12">
                        <v-btn
                          class="text-capitalize d-inline-block"
                          large
                          outlined
                          @click="onOtpBackClick"
                        >
                          {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
                        </v-btn>
                        <v-spacer />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-stepper-content>
              </v-stepper-items>
            </v-stepper>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>

import RegisterDialogMixin from '@/theme/default/components/login/RegisterDialog.js'

export default {
  name  : 'RegisterDialog',
  mixins: [RegisterDialogMixin]
}
</script>

<style scoped src="@/theme/default/components/login/RegisterDialog.css"></style>

<style scoped>
</style>

